import { doc, getDoc, setDoc, deleteField } from 'firebase/firestore';
import { db } from '@/backend/lib/firebase';

export interface AdminAlertEmail {
  email: string;
  enabled: boolean;
}

export interface EnergySettings {
  adminAlertEmails: AdminAlertEmail[];
}

/**
 * Raw settings document as stored in Firestore. May hold the current
 * `adminAlertEmails` array, an older `string[]` form, or the legacy
 * single-string `adminAlertEmail` field.
 */
interface RawEnergySettings {
  adminAlertEmails?: unknown;
  adminAlertEmail?: unknown;
}

const SETTINGS_REF = () => doc(db, 'settings', 'energy');

/**
 * Normalize any stored shape into the canonical `EnergySettings`.
 * Backwards-compat: a legacy `adminAlertEmail` string becomes a single
 * enabled entry; an `adminAlertEmails` array of plain strings becomes
 * enabled entries.
 */
export function normalizeEnergySettings(raw: RawEnergySettings | null | undefined): EnergySettings {
  if (raw && Array.isArray(raw.adminAlertEmails)) {
    const adminAlertEmails = raw.adminAlertEmails
      .map((entry): AdminAlertEmail | null => {
        if (typeof entry === 'string') {
          return { email: entry, enabled: true };
        }
        if (entry && typeof entry === 'object' && typeof (entry as AdminAlertEmail).email === 'string') {
          const e = entry as AdminAlertEmail;
          return { email: e.email, enabled: e.enabled !== false };
        }
        return null;
      })
      .filter((e): e is AdminAlertEmail => e !== null);
    return { adminAlertEmails };
  }

  if (raw && typeof raw.adminAlertEmail === 'string' && raw.adminAlertEmail.trim() !== '') {
    return { adminAlertEmails: [{ email: raw.adminAlertEmail, enabled: true }] };
  }

  return { adminAlertEmails: [] };
}

export async function getEnergySettings(): Promise<EnergySettings | null> {
  const snap = await getDoc(SETTINGS_REF());
  if (!snap.exists()) return null;
  return normalizeEnergySettings(snap.data() as RawEnergySettings);
}

export async function updateEnergySettings(data: Partial<EnergySettings>): Promise<void> {
  const payload: Record<string, unknown> = { ...data };
  // Drop the legacy single-email field once we write the new array shape.
  if (data.adminAlertEmails !== undefined) {
    payload.adminAlertEmail = deleteField();
  }
  await setDoc(SETTINGS_REF(), payload, { merge: true });
}
